import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Box, Divider, TextFieldWithLabel, Typography } from 'shared/ui'
import { getAriaLabel } from 'shared/lib'
import { BusinessAddressForm } from 'pages/settings/ui/BusinessAddressForm'
import { useTollFreeStepsContext } from 'pages/settings/pages/compliance/pages/tollFree/context/tollFreeStepsContext'
import { RegistrationErrorAlert } from 'pages/settings/pages/compliance/pages/tollFree/ui/RegistrationErrorAlert/RegistrationErrorAlert'
import { WhereCanIFindBusinessInfoButton } from 'pages/settings/ui/LinkButton/WhereCanIFindBusinessInfoButton'
import { IRSEINDocumentUploader } from 'pages/settings/pages/compliance/ui/IRSEINDocumentUploader'
import styles from './styles.module.scss'
import { WebsiteFields } from './WebsiteFields'

export const ManuallyBusinessFind = observer(() => {
  const { businessInfoStore, fieldsError, fieldsErrorCommonText } = useTollFreeStepsContext()
  const {
    einItem,
    einFormatted,
    einErrorMessage,
    einPlaceholder,
    setEinSearch,
    checkEin,
    addBusinessNameManually,
    websiteUrlLocalError,
    noWebsiteUrl,
    websiteUrl,
    setWebsiteUrl,
    validateWebsiteUrl,
    handleSetNoWebsiteUrl,
    socialMediaUrlError,
    socialMediaUrl,
    setSocialMediaUrl,
    init,
    addressFormStore,
    legalBusinessNameHelperText,
    registrationAuthorityStore,
    irsEINDocumentUploaderStore,
  } = businessInfoStore

  const { isUSLocation } = registrationAuthorityStore

  const einText = isUSLocation
    ? 'Employer Identification Number (EIN)'
    : 'Canadian Business Number (CBN)'

  useEffect(() => {
    init()
  }, [])

  return (
    <Box>
      <RegistrationErrorAlert />

      {fieldsErrorCommonText && (
        <Box>
          <Alert
            item={{ desc: fieldsErrorCommonText, type: 'error' }}
            marginBottom={24}
            marginTop={32}
          />
        </Box>
      )}

      <Box marginBottom={'24px'}>
        <TextFieldWithLabel
          errorMessage={einErrorMessage}
          labelProps={{
            label: {
              text: einText,
              rightNode: <WhereCanIFindBusinessInfoButton />,
            },
          }}
          textFieldProps={{
            className: styles.textInput,
            value: einFormatted,
            onChange: setEinSearch,
            onBlur: checkEin,
            InputProps: {
              placeholder: einPlaceholder,
            },
          }}
        />
      </Box>
      <Box marginBottom={'24px'}>
        <TextFieldWithLabel
          errorBorder={Boolean(fieldsError?.business_name)}
          errorMessage={fieldsError?.business_name}
          labelProps={{
            label: {
              text: 'Legal business name',
              rightNode: <WhereCanIFindBusinessInfoButton />,
            },
          }}
          textFieldProps={{
            className: styles.textInput,
            value: einItem?.name || '',
            onChange: addBusinessNameManually,
            InputProps: {
              placeholder: 'e.g. Example LLC',
            },
          }}
          bottomLabelProps={{
            label: legalBusinessNameHelperText
              ? {
                  text: (
                    <Typography
                      variant='text-input-helper'
                      ariaLabel={getAriaLabel('Legal business name helper')}
                    >
                      {legalBusinessNameHelperText}
                    </Typography>
                  ),
                }
              : undefined,
          }}
        />
      </Box>
      {isUSLocation && <IRSEINDocumentUploader store={irsEINDocumentUploaderStore} />}
      {isUSLocation && (
        <Divider sx={{ marginBottom: '32px', marginTop: '32px' }} orientation={'horizontal'} />
      )}
      <WebsiteFields
        {...{
          websiteUrlLocalError,
          noWebsiteUrl,
          websiteUrl,
          setWebsiteUrl,
          handleSetNoWebsiteUrl,
          validateWebsiteUrl,
          socialMediaUrlError,
          socialMediaUrl,
          setSocialMediaUrl,
          fieldsError,
        }}
      />
      <BusinessAddressForm
        addressData={addressFormStore.addressData}
        countries={addressFormStore.countries}
        activeCountryId={addressFormStore.activeCountryId}
        activeCountryInfo={addressFormStore.activeCountryInfo}
        states={addressFormStore.states}
        zipCode={addressFormStore.zipCode}
        zipLocalErrorMessage={addressFormStore.zipLocalErrorMessage}
        fieldsError={fieldsError}
        setActiveCountry={addressFormStore.setActiveCountry}
        setStreet1={addressFormStore.setStreet1}
        setStreet2={addressFormStore.setStreet2}
        setCity={addressFormStore.setCity}
        setState={addressFormStore.setState}
        setZip={addressFormStore.setZip}
        checkZipLength={addressFormStore.checkZipLength}
        countrySelectClassName={styles.countrySelectWrap}
        textInputClassName={styles.textInput}
      />
    </Box>
  )
})
